import { createAttributeManager, StyleRenderer } from '@redfox-thank-you/utils';
import { RichText, useBlockProps } from '@wordpress/block-editor';
import { Notice } from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { store as editorStore } from '@wordpress/editor';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { DownloadsSettings } from './settings';
import { generateDownloadsCSS } from './style';
import type { DownloadItem, DownloadsEditProps } from './types';

/**
 * Mock download data for preview
 */
const mockDownloads: DownloadItem[] = [
	{
		id: '1',
		name: 'Product Manual.pdf',
		file: '#',
		download_limit: 5,
		access_expires: null,
		downloads_remaining: 'unlimited',
	},
	{
		id: '2',
		name: 'Software License.txt',
		file: '#',
		download_limit: 3,
		access_expires: '2024-12-31',
		downloads_remaining: 2,
	},
	{
		id: '3',
		name: 'Bonus Materials.zip',
		file: '#',
		download_limit: 10,
		access_expires: null,
		downloads_remaining: 8,
	},
];

/**
 * Downloads block edit component
 */
export const DownloadsEdit: React.FC<DownloadsEditProps> = ({
	attributes,
	setAttributes,
}) => {
	// Create attribute manager with utility functions
	const { updateAttributes, getAttribute } = createAttributeManager(
		attributes,
		setAttributes,
	);

	// Extract settings properties with defaults using type-safe methods
	const title = getAttribute<string>(
		'settings.title.text',
		__('Downloads', 'redfox-thank-you'),
	);
	const titleLevel = getAttribute<number>('settings.title.level', 2);
	const showTitle = getAttribute<boolean>('settings.title.show', true);
	const showDownloadCount = getAttribute<boolean>(
		'settings.display.downloadCount',
		true,
	);

	// Check if we're in a thank you page
	const postType = useSelect((select) => {
		const editorSelect = select(
			editorStore,
		) as unknown as import('@wordpress/editor').EditorStore;
		return editorSelect?.getCurrentPostType?.() as string;
	}, []);

	// Generate unique block class based on attributes
	const generateBlockClass = () => {
		const cleanName = 'redfox-thank-you-downloads';
		const hash = Math.random().toString(36).substring(2, 10);
		return `redfox-thank-you-${cleanName}-${hash}`;
	};

	const [blockClass] = React.useState(() => generateBlockClass());

	const blockProps = useBlockProps({
		className: blockClass,
		style: {
			position: 'relative',
		},
	});

	// Show notice if not in redfoxwp_thank_you post type
	if ( postType !== 'redfoxwp_thank_you' ) {
		return (
			<div {...blockProps}>
				<Notice status="warning" isDismissible={false}>
					{__(
						'The Downloads block can only be used in Thank You pages.',
						'redfox-thank-you',
					)}
				</Notice>
			</div>
		);
	}

	return (
		<>
			<StyleRenderer
				css={generateDownloadsCSS(attributes, blockClass)}
				blockId={blockClass}
			/>
			<DownloadsSettings
				updateAttributes={updateAttributes}
				getAttribute={getAttribute}
			/>

			<div {...blockProps}>
				<div className="redfox-thank-you-downloads__preview">
					{showTitle && (
						<RichText
							tagName={`h${titleLevel}`}
							className="redfox-thank-you-downloads__title"
							value={title}
							onChange={(value: string) =>
								updateAttributes('settings.title.text', value)
							}
							placeholder={__(
								'Downloads',
								'redfox-thank-you',
							)}
							allowedFormats={['core/bold', 'core/italic']}
						/>
					)}

					<div className="redfox-thank-you-downloads__content">
						{mockDownloads.length > 0 ? (
							<div className="redfox-thank-you-downloads__list">
								{mockDownloads.map((downloadItem) => (
									<div
										key={downloadItem.id}
										className="redfox-thank-you-downloads__item"
									>
										<div className="redfox-thank-you-downloads__item-content">
											<div className="redfox-thank-you-downloads__item-details">
												<div className="redfox-thank-you-downloads__item-name">
													{downloadItem.name}
												</div>
												{showDownloadCount && (
													<div className="redfox-thank-you-downloads__item-meta">
														{downloadItem.downloads_remaining ===
															'unlimited'
															? __(
																'Unlimited downloads',
																'redfox-thank-you',
															)
															: __(
																'Downloads remaining: %s',
																'redfox-thank-you',
															).replace(
																'%s',
																String(
																	downloadItem.downloads_remaining,
																),
															)}
														{downloadItem.access_expires && (
															<span className="redfox-thank-you-downloads__item-expires">
																{' • '}
																{__(
																	'Expires: %s',
																	'redfox-thank-you',
																).replace(
																	'%s',
																	downloadItem.access_expires,
																)}
															</span>
														)}
													</div>
												)}
											</div>
										</div>
										<div className="redfox-thank-you-downloads__item-action">
											<button
												type="button"
												className="redfox-thank-you-downloads__download-btn"
											>
												{__(
													'Download',
													'redfox-thank-you',
												)}
											</button>
										</div>
									</div>
								))}
							</div>
						) : (
							<div className="redfox-thank-you-downloads__empty">
								<p>
									{__(
										'No downloadable products in this order.',
										'redfox-thank-you',
									)}
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
		</>
	);
};
